<i18n>
{
	"en": {
		"name": "Name",
		"transportInformation": "Transport Inforation",
		"stretches": "Stretches",
		"details": "Details",
		"customer": "Customer",
		"consignor": "Consignor",
		"consignee": "Consignee",
		"despatchParty": "DespatchParty",
		"deliveryParty": "DeliveryParty",
		"sameAsCustomer": "Same as customer",
		"sameAsConsignor": "Same as consignor",
		"sameAsConsignee": "Same as consignee",
		"freightLines": "Freight lines",
		"priceLines": "Price lines",
		"loadingInformation": "Loading information",
		"freeText": "Free text",
		"arrival": "Arrival",
		"departure": "Departure",
		"removeStretch": "Remove stretch",
		"viaAddress": "Via address",
		"duration": "Duration",
		"days": "Day | Days"
	},
	"no": {
		"name": "Navn",
		"transportInformation": "Sendingsopplysninger",
		"stretches": "Strekninger",
		"details": "Detaljer",
		"customer": "Kunde",
		"consignor": "Avsender",
		"consignee": "Mottaker",
		"despatchParty": "Fra adresse",
		"deliveryParty": "Til adresse",
		"sameAsCustomer": "Samme som kunde",
		"sameAsConsignor": "Samme som avsender",
		"sameAsConsignee": "Samme som mottaker",
		"freightLines": "Fraktlinjer",
		"priceLines": "Prislinjer",
		"loadingInformation": "Lasteinformasjon",
		"freeText": "Fritekst",
		"arrival": "Ankomst",
		"departure": "Avgang",
		"removeStretch": "Fjern strekning",
		"viaAddress": "Via adresse",
		"duration": "Varighet",
		"days": "Dag | Dager"
	}
}
</i18n>

<template>
	<div class="new-order-template">
		<t-form-group :title="$t('name')" rowGap="5px" style="grid-column: 1/2;">
			<t-text-input v-model="templateDetails.name" />
		</t-form-group>

		<t-form-group :title="$t('duration')" rowGap="5px" style="grid-column: 2/3;">
			<t-number-input
				:value="templateDetails.duration"
				:extraDisplayText="$tc('days', templateDetails.duration)"
				@focus="$event.target.select()"
				@change="onIntInput('duration', $event)"
			/>
		</t-form-group>

		<goods-types-form-input
			:selectedItem="templateDetails.goodsType"
			style="grid-column: 4/5;"
			@change="updateTemplateDetails({ key: 'goodsType', value: $event })"
		/>

		<economical-statuses-form-input
			:selectedItem="templateDetails.economicalStatus"
			style="grid-column: 1/2;"
			@change="updateTemplateDetails({ key: 'economicalStatus', value: $event })"
		/>

		<order-statuses-form-input
			:selectedItem="templateDetails.status"
			style="grid-column: 2/3;"
			@change="updateTemplateDetails({ key: 'status', value: $event })"
		/>

		<order-types-form-input
			:selectedItem="templateDetails.orderType"
			style="grid-column: 3/4;"
			@select="updateTemplateDetails({ key: 'orderType', value: $event })"
		/>

		<clerk-input
			:value="templateDetails.clerk"
			style="grid-column: 4/5;"
			@change="updateTemplateDetails({ key: 'clerk', value: $event })"
		/>

		<div class="title-wrapper">
			<span class="category-title">{{ $t('transportInformation') }}</span>
			<hr >
		</div>

		<div class="main-order-transport-information" style="grid-column: 1/5;">
			<CustomerInput
				:value="customer"
				@change="customer = $event"
			/>

			<GlobalAddressInput
				:title="$t('consignor')"
				:value="consignor"
				:disabled="sameAsCustomer"
				@change="consignor = $event"
			>
				<template #header>
					<t-checkbox
						:label="$t('sameAsCustomer')"
						:checked="sameAsCustomer"
						class="header-checkbox"
						@change="setSameAsCustomer"
					/>
				</template>
			</GlobalAddressInput>

			<GlobalAddressInput
				:title="$t('consignee')"
				:value="consignee"
				@change="consignee = $event"
			/>
		</div>

		<div class="title-wrapper">
			<span class="category-title">{{ $t('stretches') }}</span>
			<hr >
		</div>

		<div class="stretches-wrapper" style="grid-column: 1/5;">
			<div :class="{ scroll: locations.length > 1 }" class="stretches">
				<location-card :start="true" :dotOffset="locations.length ? '50%' : '32.7%'" @click="newLocation()">
					<template #beforeLine>
						<GlobalAddressInput
							:title="$t('despatchParty')"
							:value="despatchParty"
							:disabled="sameAsConsignor"
							class="address-input"
							@change="despatchParty = $event"
						>
							<template #header>
								<t-checkbox
									:label="$t('sameAsConsignor')"
									:checked="sameAsConsignor"
									class="header-checkbox"
									@change="setSameAsConsignor"
								/>
							</template>
						</GlobalAddressInput>
					</template>

					<template #afterLine>
						<div class="date-time">
							<t-time-picker
								:label="$t('departure')"
								:value="etdTime"
								style="margin-top: 10px;"
								@input="etdTime = $event"
							/>
							<t-number-input
								v-if="subOrders && subOrders.length"
								v-model="subOrders[0].duration"
								:label="$t('duration')"
								:extraDisplayText="$tc('days', subOrders[0].duration)"
								min="1"
							/>
						</div>
					</template>

				</location-card>

				<location-card v-for="(location, index) of locations" :key="index" @click="newLocation(index)">
					<template #beforeLine>
						<GlobalAddressInput
							:title="$t('viaAddress')"
							:value="location.address"
							class="address-input"
							@change="updateLocation({ index, key: 'address', value: $event })"
						>
							<template #header>
								<a href="#" @click.prevent="removeLocation(index)">{{ $t('removeStretch') }}</a>
							</template>
						</GlobalAddressInput>
					</template>

					<template #afterLine>
						<div class="date-time middle">
							<div style="margin-right: 5px;">
								<t-time-picker
									:label="$t('arrival')"
									:value="location.arrivalTime"
									style="margin-top: 10px;"
									@input="updateLocation({ index, key: 'arrivalTime', value: $event })"
								/>
							</div>
							<div style="margin-left: 5px;">
								<t-time-picker
									:label="$t('departure')"
									:value="location.departureTime"
									style="margin-top: 10px;"
									@input="updateLocation({ index, key: 'departureTime', value: $event })"
								/>
								<t-number-input
									v-if="subOrders && subOrders.length"
									v-model="subOrders[index + 1].duration"
									:label="$t('duration')"
									:extraDisplayText="$tc('days', subOrders[index + 1].duration)"
									min="1"
								/>
							</div>
						</div>
					</template>
				</location-card>

				<location-card :end="true" :dotOffset="locations.length ? '50%' : '67.3%'">
					<template #beforeLine>
						<GlobalAddressInput
							:title="$t('deliveryParty')"
							:value="deliveryParty"
							:disabled="sameAsConsignee"
							class="address-input"
							@change="deliveryParty = $event"
						>
							<template #header>
								<t-checkbox
									:label="$t('sameAsConsignee')"
									:checked="sameAsConsignee"
									class="header-checkbox"
									@change="setSameAsConsignee"
								/>
							</template>
						</GlobalAddressInput>
					</template>

					<template #afterLine>
						<div class="date-time">
							<t-time-picker
								:label="$t('arrival')"
								:value="etaTime"
								style="margin-top: 10px;"
								@input="etaTime = $event"
							/>
						</div>
					</template>

				</location-card>
			</div>
		</div>

		<div class="title-wrapper">
			<span class="category-title">{{ $t('details') }}</span>
			<hr >
		</div>

		<t-tab-view
			:tabs="tabs"
			:order="templateDetails"
			style="grid-column: 1/5; height: 400px;"
		/>
	</div>
</template>

<script>
// TODO matiaseid make this consistent with (new) newOrder.vue
import { mapState, mapMutations } from '../store/orderTemplates.store';
import { newUUID } from '@/core';
import moment from 'moment';

import GoodsTypesFormInput from '@/modules/goodsTypes/GoodsTypesFormInput.vue';
import EconomicalStatusesFormInput from '@/modules/economicalStatuses/EconomicalStatusesFormInput.vue';
import OrderStatusesFormInput from '@/modules/orderStatuses/OrderStatusesFormInput.vue';
import OrderTypesFormInput from '@/modules/orderTypes/OrderTypesFormInput.vue';
import ClerkInput from '@/modules/clerks/ClerkInput.vue';
import LocationCard from '@/modules/newOrder/LocationCard.vue';

import FreightLines from './FreightLines.vue';
import PriceLines from '@/modules/newOrder/details/PriceLines.vue';
import FreeText from './FreeText.vue';
import CustomerInput from '@/modules/customers/CustomerInput';
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';

export default {
	name: 'OrderTemplateData',
	components: {
		GlobalAddressInput,
		CustomerInput,
		GoodsTypesFormInput,
		EconomicalStatusesFormInput,
		OrderStatusesFormInput,
		OrderTypesFormInput,
		ClerkInput,
		LocationCard
	},
	data() {
		return {
			sameAsCustomer: false,
			sameAsConsignor: false,
			sameAsConsignee: false,
			locations: [],
			subOrders: []
		};
	},
	computed: {
		...mapState(['templateDetails']),
		tabs() {
			return [
				{ component: FreightLines, label: this.$t('freightLines'), events: { update: this.updateTemplateDetails } },
				{ component: PriceLines, label: this.$t('priceLines'), events: { update: this.updateTemplateDetails } },
				{ component: FreeText, label: this.$t('freeText'), events: { update: this.updateTemplateDetails } }
			];
		},
		customer: {
			get() {
				return this.templateDetails.customer;
			},
			set(value) {
				this.updateTemplateDetails({ key: 'customer', value });
				if (this.sameAsCustomer) {
					this.consignor = value;
				}
				for (let subOrder of this.subOrders) {
					subOrder.customer = value;
				}
			}
		},
		consignor: {
			get() {
				return this.templateDetails.consignor;
			},
			set(value) {
				this.updateTemplateDetails({ key: 'consignor', value });
				if (this.sameAsConsignor) {
					this.despatchParty = value;
				}
				for (let subOrder of this.subOrders) {
					subOrder.consignor = value;
				}
			}
		},
		consignee: {
			get() {
				return this.templateDetails.consignee;
			},
			set(value) {
				this.updateTemplateDetails({ key: 'consignee', value });
				if (this.sameAsConsignee) {
					this.deliveryParty = value;
				}
				for (let subOrder of this.subOrders) {
					subOrder.consignee = value;
				}
			}
		},
		despatchParty: {
			get() {
				return this.templateDetails.despatchParty;
			},
			set(value) {
				this.updateTemplateDetails({ key: 'despatchParty', value });
				if (this.subOrders[0])
					this.subOrders[0].despatchParty = value;
			}
		},
		etdTime: {
			get() {
				return this.templateDetails.departureTime ? moment(this.templateDetails.departureTime, 'HH:mm').format('LT') : '';
			},
			set(value) {
				this.updateTemplateDetails({ key: 'departureTime', value: moment(value, 'LT').format('HH:mm') });
				if (this.subOrders[0])
					this.subOrders[0].departureTime = moment(value, 'LT').format('HH:mm');
			}
		},
		deliveryParty: {
			get() {
				return this.templateDetails.deliveryParty;
			},
			set(value) {
				this.updateTemplateDetails({ key: 'deliveryParty', value });
				if (this.subOrders[this.subOrders.length - 1])
					this.subOrders[this.subOrders.length - 1].deliveryParty = value;
			}
		},
		etaTime: {
			get() {
				return this.templateDetails.arrivalTime ? moment(this.templateDetails.arrivalTime, 'HH:mm').format('LT') : '';
			},
			set(value) {
				this.updateTemplateDetails({ key: 'arrivalTime', value: moment(value, 'LT').format('HH:mm') });
				if (this.subOrders[this.subOrders.length - 1])
					this.subOrders[this.subOrders.length - 1].arrivalTime = moment(value, 'LT').format('HH:mm');
			}
		},
	},
	watch: {
		subOrders: {
			deep: true,
			handler(newVal) {
				this.updateTemplateDetails({ key: 'subOrders', value: newVal });
			}
		}
	},
	mounted() {
		this.processLocations();
	},
	beforeDestroy() {
		this.sameAsCustomer = false;
		this.sameAsConsignor = false;
		this.sameAsConsignee = false;
	},
	methods: {
		...mapMutations(['updateTemplateDetails']),
		onIntInput(key, strValue) {
			let intValue = parseInt(strValue);
			if (isNaN(intValue))
				intValue = 0;
			this.updateTemplateDetails({ key, value: intValue });
		},
		setSameAsCustomer(value) {
			this.sameAsCustomer = value;
			if (value) {
				this.consignor = this.customer;
			}
		},
		setSameAsConsignor(value) {
			this.sameAsConsignor = value;
			if (value) {
				this.despatchParty = this.consignor;
			}
		},
		setSameAsConsignee(value) {
			this.sameAsConsignee = value;
			if (value) {
				this.deliveryParty = this.consignee;
			}
		},
		processLocations() {
			this.subOrders = this.templateDetails.subOrders && this.templateDetails.subOrders.length ? JSON.parse(JSON.stringify(this.templateDetails.subOrders)) : [];
			this.subOrders.sort( (a, b) => {
				if (!a || !b) return 0;
				if (a.prev === null || (Array.isArray(a.next) && a.next.includes(b.id))) {
					return -1;
				} else if (b.prev === null || (Array.isArray(b.next) && b.next.includes(a.id))) {
					return 1;
				} else {
					return 0;
				}
			});
			if (this.subOrders.length <= 1) return;
			let locations = [];
			for (let i = 0; i < this.templateDetails.subOrders.length - 1; i++) {
				locations.push({
					address: this.subOrders[i].deliveryParty,
					arrivalTime: this.subOrders[i].arrivalTime ? moment(this.subOrders[i].arrivalTime, 'HH:mm').format('LT') : '',
					departureTime: this.subOrders[i + 1].departureTime ? moment(this.subOrders[i].departureTime, 'HH:mm').format('LT') : ''
				});
			}
			this.locations = locations;
		},
		removeLocation(index) {
			this.locations.splice(index, 1);
			this.subOrders[index] = {
				...this.subOrders[index],
				next: this.subOrders[index + 1].next,
				deliveryParty: this.subOrders[index + 1].deliveryParty,
			};
			this.subOrders.splice(index + 1,1);
			if (this.subOrders[index + 1]) this.subOrders[index + 1].prev = this.subOrders[index].id;
		},
		newLocation(index) {
			let newLocation = {
				address: { name: undefined },
				arrivalTime: '',
				departureTime: '',
			};
			if (index === undefined) {
				this.locations.unshift(newLocation);
			} else {
				this.locations.splice(index + 1, 0, newLocation);
			}
			let i = index === undefined ? 0 : index + 1;
			let newSubOrder = JSON.parse(JSON.stringify(this.subOrders[i]));
			newSubOrder = {
				...newSubOrder,
				id: newUUID(),
				prev: this.subOrders[i].id,
				next: this.subOrders[i + 1] ? this.subOrders[i + 1].id : null,
				despatchParty: { name: undefined },
				deliveryParty: this.subOrders[i].deliveryParty,
				duration: 1
			};
			this.subOrders[i] = {
				...this.subOrders[i],
				next: newSubOrder.id,
				deliveryParty: { name: undefined },
			};
			if ( this.subOrders[i + 1] ) this.subOrders[i + 1].prev = newSubOrder.id;
			this.subOrders.splice(i + 1, 0, newSubOrder);
		},
		updateLocation({ index, key, value }) {
			this.locations[index][key] = value;
			if (key === 'address') {
				this.subOrders[index].deliveryParty = value;
				this.subOrders[index + 1].despatchParty = value;
			} else {
				this.subOrders[key.includes('arrival') ? index : index + 1][key] = moment(value, 'LT').format('HH:mm');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.new-order-template {
	display: grid;
	grid-template-columns: repeat(4, minmax(200px, 1fr));
	column-gap: 20px;
	row-gap: 20px;
}

hr {
	grid-column: 1 / auto-fill;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 0;
	border: 0;
}

.category-title {
	user-select: none;
	font-size: 10px;
	font-weight: 600;
	line-height: 1.45;
	letter-spacing: 0.3px;
	color: get-color($text, 'muted');
	text-transform: uppercase;
	margin-bottom: 2px;
	padding-bottom: 0;
}

.title-wrapper {
	grid-column: 1 / auto-fill;
	> * {
		display: flex;
	}
}

.main-order-transport-information {
	display: grid;
	grid-template-columns: repeat(3, minmax(220px, 1fr));
	column-gap: 20px;
}

.header-checkbox {
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}

.stretches-wrapper {
	display: flex;
	justify-content: center;
}

.stretches {
	display: flex;
	width: 920px;

	&.scroll {
		overflow-x: scroll;
	}
}

.address-input {
	width: 280px !important;
}

.date-time {
	width: 280px;
	display: flex;
	flex-direction: column;
	align-items: center;
	> * {
		width: 150px !important;
	}

	&.middle {
		align-items: initial;
		flex-direction: row;
		justify-content: space-between;
	}
}

a {
	user-select: none;
}
</style>